/**
 * BathymetryToolCard — WP-7 (hydrographic / bathymetry surfaces).
 *
 * Implements the bathymetry slice of Card 7 from
 * `VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md` §7.7 / §8.7.
 * Activates inside the right-rail `ToolCardDock` when
 * `SceneSelection.toolCardKind === 'bathymetry'` (mapped via
 * `sceneExplorer.getToolCardKindFor` from the `bathymetry` /
 * `geophysics` layer kinds and the `terrain` slot index nodes).
 *
 * Scope (see §7.7):
 *   - Depth color ramp picker
 *   - Vertical exaggeration slider (1×–20×) with MONO readout
 *   - Contour interval slider (m) with MONO readout
 *   - Isobath visibility toggle
 *   - Opacity slider
 *   - Sounding density chip
 *   - Source survey metadata (read-only): survey id, vessel, frequency,
 *     processing pipeline (MONO for identifiers)
 *   - Provenance footer
 *   - Full state matrix: rest, loading, empty, error,
 *     permission-denied, stale
 *
 * Adapter contract:
 *   The card consumes a card-level `BathymetryAdapter` so it stays
 *   engine-agnostic. Tests use a vitest mock; the host wires a real
 *   bridge that fans out to `MapLibreAdapter.setRasterOpacity` (added
 *   in this WP as a thin pass-through) for raster bathymetry layers
 *   and to `Giro3DAdapter.setLayerOpacity` for Giro3D-rendered
 *   bathymetric meshes.
 */

import { useId, useMemo, type ReactNode } from 'react';
import {
  Banner,
  Card,
  Chip,
  Icon,
  Slider,
  Stack,
  Text,
  tokens,
  type IconName,
} from '@/design';
// WP-11 / WP-7 → WP-11 dedupe (NB-06). See GeophysicsToolCard.tsx
// for the rationale: the canonical primitive in `components/three-d`
// is the single source of truth, but the bathymetry surface contract
// wants the bare `Local grid` label + a sibling `<Chip>` for the
// survey-line frame id, so the Card overrides the `label` prop and
// renders the frame id alongside the badge.
import {
  CoordinateModeBadge,
  type CoordinateMode as CanonicalCoordinateMode,
} from '@/components/three-d/CoordinateModeBadge';

export type CoordinateMode = CanonicalCoordinateMode;

const COORDINATE_MODE_LABEL: Record<CoordinateMode, string> = {
  georeferenced: 'Georeferenced',
  local: 'Local frame',
  'local-grid': 'Local grid',
};

// ---------------------------------------------------------------------------
// Public type contract
// ---------------------------------------------------------------------------

export type BathymetryColorRamp =
  | 'depth-blue'
  | 'depth-cmocean'
  | 'depth-greyscale'
  | 'navchart';

export type SoundingDensity = 'sparse' | 'normal' | 'dense' | 'very-dense';

export type BathymetryCardState =
  | 'ready'
  | 'loading'
  | 'empty'
  | 'error'
  | 'permission-denied'
  | 'stale';

export interface BathymetryStyleState {
  readonly opacity: number; // 0..1
  readonly colorRamp: BathymetryColorRamp;
  readonly verticalExaggeration: number; // 1..20
  readonly contourIntervalM: number; // 0..500
  readonly isobathsVisible: boolean;
  readonly soundingDensity: SoundingDensity;
}

export interface BathymetrySurveyMetadata {
  readonly surveySurveyId?: string;
  readonly vessel?: string;
  readonly surveyDateIso?: string;
  /** e.g. "200 kHz / 400 kHz", "MBES R2Sonic 2024". MONO. */
  readonly frequencyLabel?: string;
  /** e.g. "CARIS HIPS 11.4", "Qimera 2.5". MONO. */
  readonly processingPipeline?: string;
}

export interface BathymetryProvenance {
  readonly source?: string;
  readonly producer?: string;
  readonly extractionMethod?: string;
  readonly contentUri?: string;
}

export interface BathymetryAdapter {
  setOpacity(layerId: string, opacity: number): void;
  setColorRamp(layerId: string, ramp: BathymetryColorRamp): void;
  setVerticalExaggeration(layerId: string, factor: number): void;
  setContourInterval(layerId: string, intervalM: number): void;
  setIsobathsVisible(layerId: string, visible: boolean): void;
  setSoundingDensity(layerId: string, density: SoundingDensity): void;
}

export interface BathymetryToolCardProps {
  layerId: string;
  layerLabel: string;
  style: BathymetryStyleState;
  onStyleChange(next: BathymetryStyleState): void;
  adapter?: BathymetryAdapter;
  surveyMetadata?: BathymetrySurveyMetadata;
  provenance?: BathymetryProvenance;
  coordinateMode?: CoordinateMode;
  coordinateFrameId?: string;
  state?: BathymetryCardState;
  errorCode?: string;
  staleSinceIso?: string;
  'data-testid'?: string;
}

/**
 * Pure handler factory exported so consumers (and tests) can wire
 * adapter calls without importing the React component. The card uses
 * this factory internally so behavior stays in lockstep.
 */
export function createBathymetryHandlers(args: {
  layerId: string;
  style: BathymetryStyleState;
  onStyleChange(next: BathymetryStyleState): void;
  adapter?: BathymetryAdapter;
}): {
  setOpacity(value: number): void;
  setColorRamp(ramp: BathymetryColorRamp): void;
  setVerticalExaggeration(value: number): void;
  setContourInterval(value: number): void;
  toggleIsobaths(): void;
  setSoundingDensity(density: SoundingDensity): void;
} {
  const { layerId, style, onStyleChange, adapter } = args;
  const patch = (partial: Partial<BathymetryStyleState>) =>
    onStyleChange({ ...style, ...partial });
  return {
    setOpacity(value) {
      patch({ opacity: value });
      adapter?.setOpacity(layerId, value);
    },
    setColorRamp(ramp) {
      patch({ colorRamp: ramp });
      adapter?.setColorRamp(layerId, ramp);
    },
    setVerticalExaggeration(value) {
      patch({ verticalExaggeration: value });
      adapter?.setVerticalExaggeration(layerId, value);
    },
    setContourInterval(value) {
      patch({ contourIntervalM: value });
      adapter?.setContourInterval(layerId, value);
    },
    toggleIsobaths() {
      const next = !style.isobathsVisible;
      patch({ isobathsVisible: next });
      adapter?.setIsobathsVisible(layerId, next);
    },
    setSoundingDensity(density) {
      patch({ soundingDensity: density });
      adapter?.setSoundingDensity(layerId, density);
    },
  };
}

// ---------------------------------------------------------------------------
// Static label tables
// ---------------------------------------------------------------------------

const COLOR_RAMP_OPTIONS: ReadonlyArray<{ id: BathymetryColorRamp; label: string }> = [
  { id: 'depth-blue', label: 'Blue depth' },
  { id: 'depth-cmocean', label: 'cmocean deep' },
  { id: 'depth-greyscale', label: 'Greyscale' },
  { id: 'navchart', label: 'Nav chart' },
];

const DENSITY_OPTIONS: ReadonlyArray<{
  id: SoundingDensity;
  label: string;
  variant: 'neutral' | 'info' | 'warning';
}> = [
  { id: 'sparse', label: 'Sparse', variant: 'neutral' },
  { id: 'normal', label: 'Normal', variant: 'info' },
  { id: 'dense', label: 'Dense', variant: 'info' },
  { id: 'very-dense', label: 'Very dense', variant: 'warning' },
];

const RANGE = {
  opacity: { min: 0, max: 1, step: 0.05 },
  exaggeration: { min: 1, max: 20, step: 0.5 },
  contourM: { min: 0, max: 500, step: 1 },
} as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BathymetryToolCard({
  layerId,
  layerLabel,
  style,
  onStyleChange,
  adapter,
  surveyMetadata,
  provenance,
  coordinateMode = 'georeferenced',
  coordinateFrameId,
  state = 'ready',
  errorCode,
  staleSinceIso,
  'data-testid': testId,
}: BathymetryToolCardProps) {
  const titleId = useId();

  const handlers = useMemo(
    () =>
      createBathymetryHandlers({
        layerId,
        style,
        onStyleChange,
        adapter,
      }),
    [layerId, style, onStyleChange, adapter],
  );

  const handleOpacity = handlers.setOpacity;
  const handleColorRamp = handlers.setColorRamp;
  const handleExaggeration = handlers.setVerticalExaggeration;
  const handleContourInterval = handlers.setContourInterval;
  const handleIsobaths = handlers.toggleIsobaths;
  const handleDensity = handlers.setSoundingDensity;

  const stateBanner = useMemo(
    () => renderStateBanner(state, errorCode, staleSinceIso),
    [state, errorCode, staleSinceIso],
  );

  const interactive = state === 'ready';

  return (
    <Card
      data-testid={testId ?? 'bathymetry-tool-card'}
      header={
        <Stack direction="row" align="center" gap={2}>
          <Icon name="waves" size={24} decorative />
          <span id={titleId}>{layerLabel}</span>
        </Stack>
      }
      footer={renderProvenanceFooter(provenance)}
    >
      <Stack direction="column" gap={3} aria-labelledby={titleId}>
        <Stack direction="row" gap={2} style={{ flexWrap: 'wrap' }}>
          <CoordinateModeBadge
            mode={coordinateMode}
            label={COORDINATE_MODE_LABEL[coordinateMode]}
          />
          {coordinateFrameId ? (
            <Chip
              icon="my_location"
              label={coordinateFrameId}
              variant="neutral"
              aria-label={`Coordinate frame: ${coordinateFrameId}`}
            />
          ) : null}
          <Chip
            label={style.isobathsVisible ? 'Isobaths on' : 'Isobaths off'}
            icon={style.isobathsVisible ? 'visibility' : 'visibility_off'}
            variant={style.isobathsVisible ? 'success' : 'neutral'}
          />
        </Stack>

        {stateBanner}

        {interactive ? (
          <>
            {renderColorRampSection(style.colorRamp, handleColorRamp)}
            {renderSlider({
              label: 'Opacity',
              icon: 'tune',
              value: style.opacity,
              range: RANGE.opacity,
              unit: '',
              valueLabel: formatPercent(style.opacity),
              onChange: handleOpacity,
            })}
            {renderSlider({
              label: 'Vertical exaggeration',
              icon: 'terrain',
              value: style.verticalExaggeration,
              range: RANGE.exaggeration,
              unit: '×',
              valueLabel: `${style.verticalExaggeration.toFixed(1)}×`,
              onChange: handleExaggeration,
            })}
            {renderSlider({
              label: 'Contour interval',
              icon: 'straighten',
              value: style.contourIntervalM,
              range: RANGE.contourM,
              unit: 'm',
              valueLabel: `${style.contourIntervalM.toFixed(0)} m`,
              onChange: handleContourInterval,
            })}
            {renderIsobathToggle(style.isobathsVisible, handleIsobaths)}
            {renderSoundingDensitySection(style.soundingDensity, handleDensity)}
          </>
        ) : null}

        {renderSurveyMetadataCard(surveyMetadata)}
      </Stack>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Sub-renderers
// ---------------------------------------------------------------------------

function renderStateBanner(
  state: BathymetryCardState,
  errorCode: string | undefined,
  staleSinceIso: string | undefined,
): ReactNode {
  if (state === 'ready') return null;
  if (state === 'loading') {
    return (
      <Banner
        severity="info"
        title="Loading bathymetry layer"
        body="Fetching depth grid and survey metadata."
        narrationKey="bathymetry:loading"
        persistent={false}
      />
    );
  }
  if (state === 'empty') {
    return (
      <Banner
        severity="info"
        title="No bathymetry layer selected"
        body="Pick a bathymetry layer in the scene tree to start tuning."
        narrationKey="bathymetry:empty"
        persistent={false}
      />
    );
  }
  if (state === 'error') {
    return (
      <Banner
        severity="error"
        title="Failed to load bathymetry layer"
        body="Check the source endpoint and retry."
        errorCode={errorCode}
        narrationKey="bathymetry:error"
      />
    );
  }
  if (state === 'permission-denied') {
    return (
      <Banner
        severity="warning"
        title="Permission denied"
        body="You do not have access to this bathymetry layer in the current tenant."
        narrationKey="bathymetry:permission"
      />
    );
  }
  if (state === 'stale') {
    return (
      <Banner
        severity="warning"
        title="Stale tiles"
        body={
          staleSinceIso
            ? (
              <span>
                Last updated{' '}
                <span
                  style={{
                    fontFamily: tokens.font.family.mono,
                    fontSize: tokens.font.role.engMono.size,
                  }}
                >
                  {staleSinceIso}
                </span>
              </span>
            )
            : 'Depth grid cache is older than the source survey.'
        }
        narrationKey="bathymetry:stale"
      />
    );
  }
  return null;
}

function renderColorRampSection(
  selected: BathymetryColorRamp,
  onSelect: (ramp: BathymetryColorRamp) => void,
): ReactNode {
  return (
    <Stack direction="column" gap={1}>
      <Stack direction="row" align="center" gap={1}>
        <Icon name="palette" size={20} decorative />
        <Text variant="caption" as="span">Depth color ramp</Text>
      </Stack>
      <div
        role="radiogroup"
        aria-label="Depth color ramp"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: tokens.space[1],
        }}
      >
        {COLOR_RAMP_OPTIONS.map((option) => {
          const isSelected = option.id === selected;
          return (
            <Chip
              key={option.id}
              label={option.label}
              icon="palette"
              variant={isSelected ? 'tertiary' : 'neutral'}
              pressable
              pressed={isSelected}
              onClick={() => onSelect(option.id)}
              aria-label={`Depth color ramp ${option.label}`}
              data-testid={`bathymetry-ramp-${option.id}`}
            />
          );
        })}
      </div>
    </Stack>
  );
}

function renderIsobathToggle(
  visible: boolean,
  onToggle: () => void,
): ReactNode {
  return (
    <Stack direction="column" gap={1}>
      <Stack direction="row" align="center" gap={1}>
        <Icon name="layers" size={20} decorative />
        <Text variant="caption" as="span">Isobaths</Text>
      </Stack>
      <Chip
        label={visible ? 'Show isobaths' : 'Hide isobaths'}
        icon={visible ? 'visibility' : 'visibility_off'}
        variant={visible ? 'success' : 'neutral'}
        pressable
        pressed={visible}
        onClick={onToggle}
        aria-label="Toggle isobaths"
        data-testid="bathymetry-isobaths-toggle"
      />
    </Stack>
  );
}

function renderSoundingDensitySection(
  selected: SoundingDensity,
  onSelect: (density: SoundingDensity) => void,
): ReactNode {
  return (
    <Stack direction="column" gap={1}>
      <Stack direction="row" align="center" gap={1}>
        <Icon name="graphic_eq" size={20} decorative />
        <Text variant="caption" as="span">Sounding density</Text>
      </Stack>
      <div
        role="radiogroup"
        aria-label="Sounding density"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: tokens.space[1],
        }}
      >
        {DENSITY_OPTIONS.map((option) => {
          const isSelected = option.id === selected;
          return (
            <Chip
              key={option.id}
              label={option.label}
              icon="graphic_eq"
              variant={isSelected ? 'tertiary' : option.variant}
              pressable
              pressed={isSelected}
              onClick={() => onSelect(option.id)}
              aria-label={`Sounding density ${option.label}`}
              data-testid={`bathymetry-density-${option.id}`}
            />
          );
        })}
      </div>
    </Stack>
  );
}

interface SliderRowOptions {
  label: string;
  icon: IconName;
  value: number;
  range: { min: number; max: number; step: number };
  unit: string;
  valueLabel: string;
  onChange(value: number): void;
}

function renderSlider({
  label,
  icon,
  value,
  range,
  unit,
  valueLabel,
  onChange,
}: SliderRowOptions): ReactNode {
  const ariaLabel = unit ? `${label} (${unit})` : label;
  return (
    <Stack direction="column" gap={1}>
      <Stack direction="row" align="center" justify="space-between" gap={2}>
        <Stack direction="row" align="center" gap={1}>
          <Icon name={icon} size={20} decorative />
          <Text variant="caption" as="span">
            {label}
            {unit ? ` (${unit})` : ''}
          </Text>
        </Stack>
        <span
          style={{
            fontFamily: tokens.font.family.mono,
            fontSize: tokens.font.role.engMono.size,
            color: tokens.color.ink.secondary,
          }}
          data-testid={`bathymetry-${slug(label)}-readout`}
        >
          {valueLabel}
        </span>
      </Stack>
      <div style={{ minHeight: tokens.density.touchMin, display: 'flex', alignItems: 'center' }}>
        <Slider
          aria-label={ariaLabel}
          min={range.min}
          max={range.max}
          step={range.step}
          value={value}
          onChange={onChange}
          data-testid={`bathymetry-${slug(label)}-slider`}
        />
      </div>
    </Stack>
  );
}

function renderSurveyMetadataCard(meta: BathymetrySurveyMetadata | undefined): ReactNode {
  if (!meta) return null;
  const rows: Array<{ label: string; value: string; mono?: boolean }> = [];
  if (meta.surveySurveyId) rows.push({ label: 'Survey', value: meta.surveySurveyId, mono: true });
  if (meta.vessel) rows.push({ label: 'Vessel', value: meta.vessel });
  if (meta.surveyDateIso) rows.push({ label: 'Survey date', value: meta.surveyDateIso, mono: true });
  if (meta.frequencyLabel) rows.push({ label: 'Frequency', value: meta.frequencyLabel, mono: true });
  if (meta.processingPipeline) rows.push({ label: 'Pipeline', value: meta.processingPipeline, mono: true });
  if (rows.length === 0) return null;
  return (
    <section
      aria-label="Source survey metadata"
      data-testid="bathymetry-survey-metadata"
      style={{
        border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
        borderRadius: tokens.radius.md,
        padding: tokens.space[2],
        background: tokens.color.surface[1],
      }}
    >
      <Stack direction="row" align="center" gap={1} style={{ marginBottom: tokens.space[1] }}>
        <Icon name="info" size={20} decorative />
        <Text variant="caption" as="span">Source survey metadata (read-only)</Text>
      </Stack>
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          rowGap: tokens.space[1],
          columnGap: tokens.space[2],
          margin: 0,
        }}
      >
        {rows.map((row) => (
          <div key={row.label} style={{ display: 'contents' }}>
            <dt
              style={{
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.label.size,
                color: tokens.color.ink.muted,
                margin: 0,
              }}
            >
              {row.label}
            </dt>
            <dd
              style={{
                margin: 0,
                fontFamily: row.mono ? tokens.font.family.mono : tokens.font.family.ui,
                fontSize: row.mono
                  ? tokens.font.role.engMono.size
                  : tokens.font.role.body.size,
                color: tokens.color.ink.primary,
              }}
            >
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function renderProvenanceFooter(provenance: BathymetryProvenance | undefined): ReactNode {
  if (!provenance || (!provenance.source && !provenance.producer && !provenance.extractionMethod && !provenance.contentUri)) {
    return null;
  }
  const parts: ReactNode[] = [];
  if (provenance.source) parts.push(<span key="src">Source: {provenance.source}</span>);
  if (provenance.producer) parts.push(<span key="prod">Producer: {provenance.producer}</span>);
  if (provenance.extractionMethod) parts.push(<span key="ext">Method: {provenance.extractionMethod}</span>);
  if (provenance.contentUri) {
    parts.push(
      <span
        key="uri"
        style={{
          fontFamily: tokens.font.family.mono,
          fontSize: tokens.font.role.engMeta.size,
        }}
      >
        {provenance.contentUri}
      </span>,
    );
  }
  return (
    <Stack direction="row" align="center" gap={2} style={{ flexWrap: 'wrap' }}>
      <Icon name="info" size={16} decorative />
      {parts.map((node, idx) => (
        <span key={idx} style={{ display: 'inline-flex', alignItems: 'center' }}>
          {idx > 0 ? <span style={{ marginRight: tokens.space[1] }}>·</span> : null}
          {node}
        </span>
      ))}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function slug(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}
